import { useState } from "react";
import { useEnvironment } from "@/shared/env";
import { ChevronRight, Menu } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, cn, Sheet, SheetTrigger, SheetContent } from "@sonify/web-ui";
import { adminFetch } from "@/api/client";
import { AdminQueryKeys } from "@/api/query-keys";
import { SonifyLogo } from "@/shared/components/sonify-logo";
import { UserMenu } from "./user-menu";
import { TopNav, NAV_CONFIG, isGroup } from "./top-nav";

type ServiceStatus = "ok" | "degraded" | "down";

interface HealthResponse {
  status: ServiceStatus;
  timestamp: string;
  services: {
    database: ServiceStatus;
    jobQueue: ServiceStatus;
  };
  diagnostics: {
    failedProcessing: number;
    failedAnalysis: number;
    stuckEmbeddings: number;
    pendingJobs: number;
  };
}

function useHealthQuery() {
  return useQuery({
    queryKey: AdminQueryKeys.system.health,
    queryFn: () => adminFetch<HealthResponse>({ url: "/admin/system/health", method: "GET" }),
    refetchInterval: 30_000,
    retry: 1,
  });
}

const STATUS_DOT_COLOR: Record<ServiceStatus, string> = {
  ok: "bg-success shadow-[0_0_4px_rgba(51,199,90,0.5)]",
  degraded: "bg-warning shadow-[0_0_4px_rgba(255,149,0,0.5)]",
  down: "bg-error shadow-[0_0_4px_rgba(255,59,48,0.5)]",
};

const STATUS_TEXT_COLOR: Record<ServiceStatus, string> = {
  ok: "text-success",
  degraded: "text-warning",
  down: "text-error",
};

interface StatusIndicatorProps {
  label: string;
  status: ServiceStatus | undefined;
  value?: string | number;
}

function StatusIndicator({ label, status, value }: StatusIndicatorProps) {
  const dotColor = status ? STATUS_DOT_COLOR[status] : "bg-foreground-muted/50";
  const textColor = status ? STATUS_TEXT_COLOR[status] : "text-foreground-muted";

  return (
    <div className="flex items-center gap-2 rounded-md bg-surface-elevated/60 px-3 py-1.5">
      <span className={cn("inline-block h-2 w-2 rounded-full shrink-0", dotColor)} />
      <span className="text-foreground-muted">{label}</span>
      {value !== undefined && (
        <span className={cn("font-medium tabular-nums", textColor)}>{value}</span>
      )}
    </div>
  );
}

export function Header() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const env = useEnvironment();
  const { data: health } = useHealthQuery();

  const totalFailed = health
    ? health.diagnostics.failedProcessing + health.diagnostics.failedAnalysis
    : 0;

  return (
    <header
      className={cn(
        "sticky top-0 z-10 h-14 border-b border-border bg-background-secondary flex items-stretch justify-between px-4 gap-4",
        env.display.accentClass,
      )}
    >
      {/* Left: brand + mobile hamburger */}
      <div className="flex items-center gap-3 shrink-0">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Toggle menu">
              <Menu size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" size="sm" showClose>
            <nav className="flex flex-col gap-1 px-3 py-4">
              {NAV_CONFIG.map((entry) => {
                if (!isGroup(entry)) {
                  const Icon = entry.icon;
                  return (
                    <NavLink
                      key={entry.path}
                      to={entry.path}
                      end={entry.path === "/"}
                      onClick={() => setMobileNavOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-surface-overlay text-foreground"
                            : "text-foreground-muted hover:bg-surface-elevated hover:text-foreground",
                        )
                      }
                    >
                      <Icon size={16} />
                      {entry.label}
                    </NavLink>
                  );
                }

                const GroupIcon = entry.icon;
                const isExpanded = expandedGroup === entry.label;
                return (
                  <div key={entry.label}>
                    <button
                      onClick={() => setExpandedGroup(isExpanded ? null : entry.label)}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground-muted hover:bg-surface-elevated hover:text-foreground transition-colors"
                    >
                      <GroupIcon size={16} />
                      {entry.label}
                      <ChevronRight
                        size={14}
                        className={cn("ml-auto transition-transform", isExpanded && "rotate-90")}
                      />
                    </button>
                    {isExpanded && (
                      <div className="ml-4 flex flex-col gap-0.5">
                        {entry.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              onClick={() => setMobileNavOpen(false)}
                              className={({ isActive }) =>
                                cn(
                                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                                  isActive
                                    ? "bg-surface-overlay text-foreground"
                                    : "text-foreground-muted hover:bg-surface-elevated hover:text-foreground",
                                )
                              }
                            >
                              <ItemIcon size={16} />
                              {item.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>
        <Link to="/" className="flex items-center">
          <SonifyLogo className="h-6 w-auto text-foreground-accent" />
        </Link>
        {env.display.badgeClass && (
          <span
            className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded", env.display.badgeClass)}
          >
            {env.display.label}
          </span>
        )}
      </div>

      {/* Center: top nav (desktop only) — stretches full height for bottom-aligned border */}
      <TopNav />

      {/* Right: health indicators + user */}
      <div className="flex items-stretch gap-2 text-xs shrink-0">
        <div className="hidden sm:flex items-center gap-2">
          <StatusIndicator label="API" status={health?.status} />
          <StatusIndicator label="DB" status={health?.services.database} />
          <StatusIndicator
            label="Jobs"
            status={health?.services.jobQueue}
            value={health?.diagnostics.pendingJobs}
          />

          {totalFailed > 0 && (
            <Link
              to="/diagnostics"
              className="flex items-center gap-2 rounded-md bg-error-background px-3 py-1.5 text-error font-medium hover:bg-error-background/80 transition-colors"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-error shadow-[0_0_4px_rgba(255,59,48,0.5)]" />
              {totalFailed} failed
            </Link>
          )}
        </div>

        <div className="border-l border-border-secondary flex items-center pl-3 ml-1">
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
